import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { topics } from '@/mocks/topics';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '综艺', to: '/category/variety' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const overlay = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const linkBase = 'relative px-3 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap';
  const linkColor = overlay
    ? 'text-background-100 hover:text-background-50'
    : 'text-foreground-700 hover:text-primary-600';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        overlay ? 'bg-transparent' : 'bg-background-50/95 backdrop-blur border-b border-background-200'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[68px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center">
              <i className="ri-film-line text-background-50 text-xl" />
            </span>
            <span className={`font-display text-xl font-extrabold tracking-tight ${overlay ? 'text-background-50' : 'text-foreground-950'}`}>
              久久热
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `${linkBase} ${linkColor} ${isActive ? (overlay ? 'text-background-50' : 'text-primary-600') : ''}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {/* 专题 dropdown */}
            <div className="relative group">
              <span className={`${linkBase} ${linkColor} inline-flex items-center gap-1`}>
                专题
                <i className="ri-arrow-down-s-line text-base" />
              </span>
              <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="w-64 p-2 rounded-lg bg-background-50 border border-background-200 shadow-sm">
                  {topics.map((t) => (
                    <Link
                      key={t.id}
                      to={`/topic/${t.id}`}
                      className="block px-3 py-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="block text-sm font-medium text-foreground-900">{t.title}</span>
                      <span className="mt-0.5 block text-xs text-foreground-500 truncate">{t.subtitle}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <NavLink to="/about" className={({ isActive }) => `${linkBase} ${linkColor} ${isActive ? (overlay ? 'text-background-50' : 'text-primary-600') : ''}`}>
              关于
            </NavLink>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden md:block">
              <div className={`flex items-center gap-2 h-9 pl-3 pr-1 rounded-full border transition-all ${overlay ? 'border-background-100/40 bg-background-50/10' : 'border-background-300 bg-background-100'}`}>
                <i className={`ri-search-line text-sm ${overlay ? 'text-background-100' : 'text-foreground-500'}`} />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影片、剧集、演员"
                  className={`w-40 xl:w-52 bg-transparent text-sm outline-none ${overlay ? 'text-background-50 placeholder:text-background-200/70' : 'text-foreground-900 placeholder:text-foreground-400'}`}
                />
                <button
                  type="submit"
                  className="h-7 px-3 rounded-full bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </div>
            </form>
            <button
              type="button"
              aria-label="搜索"
              onClick={() => setSearchOpen((v) => !v)}
              className={`md:hidden w-9 h-9 rounded-full flex items-center justify-center cursor-pointer ${overlay ? 'text-background-50' : 'text-foreground-800'}`}
            >
              <i className="ri-search-line text-lg" />
            </button>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMobileOpen((v) => !v)}
              className={`lg:hidden w-9 h-9 rounded-full flex items-center justify-center cursor-pointer ${overlay ? 'text-background-50' : 'text-foreground-800'}`}
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile search expanded */}
      {searchOpen && (
        <div className={`md:hidden px-4 pb-3 ${overlay ? '' : 'border-t border-background-200'}`}>
          <form onSubmit={submitSearch} className="flex items-center gap-2 h-10 px-3 rounded-md border border-background-300 bg-background-100">
            <i className="ri-search-line text-sm text-foreground-500" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、剧集、演员"
              className="flex-1 bg-transparent text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
            />
            <button type="submit" className="text-sm font-medium text-primary-600 cursor-pointer whitespace-nowrap">搜索</button>
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50">
          <nav className="px-4 py-3 flex flex-col">
            {[...navItems, { label: '关于我们', to: '/about' }].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${isActive ? 'text-primary-600 bg-primary-50' : 'text-foreground-700 hover:bg-background-100'}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-2 pt-3 border-t border-background-200">
              <p className="px-3 text-xs font-semibold text-foreground-400 tracking-wider">精选专题</p>
              {topics.map((t) => (
                <Link key={t.id} to={`/topic/${t.id}`} className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer">
                  {t.title}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}